import * as T from "three";
import * as C from "cannon-es";
import { ZONES, SPAWN, canStand, ROUTES, FOCUS_OFFSETS } from "./config.js";
import {
  RADIUS,
  SPEED,
  surface,
  normalAt,
  coordinates,
  frameAt,
  place,
  stepSurface,
  distance,
  offsetPoint,
  pathDistance,
} from "./planet.js";
const UP = new T.Vector3(0, 1, 0);
export class World {
  constructor(scene, assets, low) {
    Object.assign(this, { scene, assets, low, elapsed: 0, reactUntil: 0 });
    this.zones = new Map();
    this.zoneTasks = new Map();
    this.interactive = [];
    this.decorations = [];
    this.decorationNames = new Set();
    this.effects = [];
    this.obstacles = [];
    this.fadeCamera = { value: new T.Vector3() };
    this.fadeTarget = { value: new T.Vector3() };
    this.fadeEnabled = { value: 0 };
    this.position = surface(SPAWN);
    this.safe = this.position.clone();
    this.north = frameAt(SPAWN).north;
    this.heading = this.north.clone().negate();
    this.ground = new T.Group();
    scene.add(this.ground);
    this.materials = {};
    for (const [name, color] of Object.entries({
      grass: "#96b879",
      path: "#f2e4bb",
      lake: "#7cbdc4",
      cream: "#fff0d1",
      wood: "#ad7950",
      pink: "#eaae99",
      leaf: "#658c64",
      yellow: "#f4cf79",
    }))
      this.materials[name] = new T.MeshStandardMaterial({
        color,
        roughness: 0.95,
      });
    this.setGround();
    this.setPhysics();
  }
  setGround() {
    const geometry = new T.SphereGeometry(RADIUS, 96, 64);
    const colors = [],
      p = geometry.attributes.position;
    const a = new T.Color("#9cbf7f"),
      b = new T.Color("#b2c98b");
    for (let i = 0; i < p.count; i++) {
      const n = new T.Vector3().fromBufferAttribute(p, i).normalize();
      const t =
        0.5 + 0.22 * Math.sin(n.x * 8 + n.y * 3) * Math.cos(n.z * 9 - n.y * 4);
      const c = a.clone().lerp(b, t);
      colors.push(c.r, c.g, c.b);
    }
    geometry.setAttribute("color", new T.Float32BufferAttribute(colors, 3));
    this.planet = new T.Mesh(
      geometry,
      new T.MeshStandardMaterial({ vertexColors: true, roughness: 1 }),
    );
    this.planet.receiveShadow = true;
    this.ground.add(this.planet);
    this.routes = [];
    for (const route of ROUTES) {
      const curve = new T.CatmullRomCurve3(route.points.map(p => surface(p)), false, "centripetal");
      const points = curve.getPoints(route.points.length * 32).map(p => p.normalize().multiplyScalar(RADIUS));
      this.road(points, route.width);
      for (let i = 1; i < points.length; i++) this.routes.push({ from: coordinates(points[i - 1]), to: coordinates(points[i]), width: route.width });
    }
    for (const z of ZONES) {
      const sizes = { welcome: [3.7, 2.1], inspiration: [4.3, 3.8], studio: [3.8, 3.2], works: [4.7, 2.7], share: [2.7, 2.3], pocket: [3.5, 2.8], quiet: [2.7, 2.1] };
      this.patch(z.point, sizes[z.id], this.materials.path);
      this.path(z.spawn, z.focus, 1.65);
    }
    this.pondPoint = offsetPoint(ZONES.find(z => z.id === "share").point, 0, -4.2);
    this.patch(this.pondPoint, [4.6, 4.7], this.materials.cream);
    this.patch(this.pondPoint, [4.2, 4.3], this.materials.lake, 0.085);
    this.clouds = new T.Group();
    this.scene.add(this.clouds);
    const cloudMat = new T.MeshStandardMaterial({
      color: "#fff9ea",
      roughness: 1,
    });
    for (const [point, scale] of [
      [[110, 65], 0.7],
      [[-115, 55], 0.8],
      [[170, -55], 0.7],
      [[-50, -70], 0.65],
    ]) {
      const cloud = new T.Group();
      for (let j = 0; j < 4; j++) {
        const m = new T.Mesh(new T.SphereGeometry(1, 12, 8), cloudMat);
        m.position.set((j - 1.5) * 1.3, Math.sin(j * 1.8) * 0.3, 0);
        m.scale.set(1.4, j === 1 ? 1.1 : 0.7, 0.9);
        cloud.add(m);
      }
      place(cloud, point, 12, scale);
      this.clouds.add(cloud);
    }
  }
  patch(point, size, material, height = 0.06) {
    const [sizeX, sizeZ] = Array.isArray(size) ? size : [size, size];
    const center = surface(point),
      { right, north } = frameAt(point),
      vertices = [],
      indices = [];
    const rings = 12,
      segments = 64;
    for (let r = 0; r <= rings; r++)
      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const v = center
          .clone()
          .addScaledVector(right, (Math.cos(angle) * sizeX * r) / rings)
          .addScaledVector(north, (Math.sin(angle) * sizeZ * r) / rings);
        vertices.push(
          ...v
            .normalize()
            .multiplyScalar(RADIUS + height)
            .toArray(),
        );
        if (r < rings && i < segments) {
          const a = r * (segments + 1) + i,
            b = a + segments + 1;
          indices.push(a, b + 1, b, a, a + 1, b + 1);
        }
      }
    const g = new T.BufferGeometry();
    g.setAttribute("position", new T.Float32BufferAttribute(vertices, 3));
    g.setIndex(indices);
    g.computeVertexNormals();
    const m = new T.Mesh(g, material);
    m.material.side = T.DoubleSide;
    m.receiveShadow = true;
    this.ground.add(m);
    return m;
  }
  path(from, to, width = 1.7) {
    const a = normalAt(from),
      b = normalAt(to),
      angle = a.angleTo(b),
      vertices = [],
      indices = [];
    const axis = new T.Vector3().crossVectors(a, b).normalize();
    const steps = Math.max(8, Math.ceil(angle * RADIUS * 2));
    for (let i = 0; i <= steps; i++) {
      const n = a.clone().applyAxisAngle(axis, (angle * i) / steps);
      for (const side of [-1, 1])
        vertices.push(
          ...n
            .clone()
            .multiplyScalar(RADIUS)
            .addScaledVector(axis, (side * width) / 2)
            .normalize()
            .multiplyScalar(RADIUS + 0.06)
            .toArray(),
        );
      if (i < steps) {
        const j = i * 2;
        indices.push(j, j + 1, j + 2, j + 1, j + 3, j + 2);
      }
    }
    const g = new T.BufferGeometry();
    g.setAttribute("position", new T.Float32BufferAttribute(vertices, 3));
    g.setIndex(indices);
    g.computeVertexNormals();
    const m = new T.Mesh(g, this.materials.path);
    m.material.side = T.DoubleSide;
    m.receiveShadow = true;
    this.ground.add(m);
  }
  road(points, width) {
    const vertices = [], indices = [];
    for (let i = 0; i < points.length; i++) {
      const normal = points[i].clone().normalize();
      const tangent = points[Math.min(i + 1, points.length - 1)].clone().sub(points[Math.max(i - 1, 0)]);
      const side = new T.Vector3().crossVectors(tangent, normal).normalize();
      for (const sign of [-1, 1]) vertices.push(...points[i].clone().addScaledVector(side, sign * width / 2).normalize().multiplyScalar(RADIUS + 0.095).toArray());
      if (i < points.length - 1) { const j = i * 2; indices.push(j, j + 1, j + 2, j + 1, j + 3, j + 2); }
    }
    const geometry = new T.BufferGeometry();
    geometry.setAttribute("position", new T.Float32BufferAttribute(vertices, 3)); geometry.setIndex(indices); geometry.computeVertexNormals();
    const mesh = new T.Mesh(geometry, this.materials.path); mesh.receiveShadow = true; this.ground.add(mesh);
  }
  setPhysics() {
    this.physics = new C.World({ gravity: new C.Vec3(0, 0, 0) });
    this.physics.defaultContactMaterial.friction = 0;
    this.physics.defaultContactMaterial.restitution = 0;
    this.physics.addBody(new C.Body({ mass: 0, shape: new C.Sphere(RADIUS) }));
    this.body = new C.Body({
      mass: 1,
      shape: new C.Sphere(0.4),
      linearDamping: 0,
      fixedRotation: true,
    });
    const p = this.position.clone().setLength(RADIUS + 0.4);
    this.body.position.set(p.x, p.y, p.z);
    this.physics.addBody(this.body);
  }
  async character() {
    if (!this.characterTask)
      this.characterTask = this.buildCharacter().catch((error) => {
        this.characterTask = null;
        throw error;
      });
    return this.characterTask;
  }
  async buildCharacter() {
    this.avatar = await this.assets.instance("girl");
    this.avatar.scale.setScalar(1.5);
    place(this.avatar, coordinates(this.position), 0, 1.5);
    this.scene.add(this.avatar);
    this.mixer = new T.AnimationMixer(this.avatar);
    this.actions = Object.fromEntries(
      this.avatar.animations.map((clip) => [
        clip.name,
        this.mixer.clipAction(clip),
      ]),
    );
    this.play("Idle");
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 64;
    const ctx = canvas.getContext("2d"),
      g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, "rgba(255,233,143,.85)");
    g.addColorStop(0.2, "rgba(255,226,132,.4)");
    g.addColorStop(1, "rgba(255,226,132,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    this.spark = new T.Sprite(
      new T.SpriteMaterial({
        map: new T.CanvasTexture(canvas),
        transparent: true,
        depthWrite: false,
        blending: T.AdditiveBlending,
      }),
    );
    this.spark.visible = false;
    this.spark.scale.setScalar(0.5);
    this.spark.position.set(0.033, -0.73, 0);
    this.avatar.getObjectByName("RightArm")?.add(this.spark);
  }
  play(name) {
    if (!this.actions || this.animation === name) return;
    const old = this.actions[this.animation];
    this.animation = name;
    this.actions[name].reset().play();
    if (old) this.actions[name].crossFadeFrom(old, 0.3, true);
  }
  async model(name, height) {
    const object = await this.assets.instance(name);
    if (height) {
      const box = new T.Box3().setFromObject(object);
      const size = box.getSize(new T.Vector3());
      const c = box.getCenter(new T.Vector3());
      const wrapper = new T.Group();
      object.position.sub(new T.Vector3(c.x, box.min.y, c.z));
      wrapper.add(object);
      wrapper.scale.setScalar(height / Math.max(0.01, size.y));
      return wrapper;
    }
    return object;
  }
  async cottage() {
    const group = new T.Group();
    // Four authored Kenney wall modules form a two-unit room. +X is the source facade normal.
    for (const [name, x, z, turn] of [
      ["wall-door", 0, 0.5, -Math.PI / 2],
      ["wall-window-round", 0.5, 0, 0],
      ["wall", 0, -0.5, Math.PI / 2],
      ["wall-window-round", -0.5, 0, Math.PI],
    ]) {
      const wall = await this.model(`free/${name}`);
      wall.position.set(x, 0, z);
      wall.rotation.y = turn;
      wall.scale.set(1, 2, 2);
      group.add(wall);
    }
    const foundation = this.box([2.6, 0.28, 2.6], this.materials.cream);
    foundation.position.y = -0.08;
    group.add(foundation);
    const roof = await this.model("free/roof-gable");
    roof.scale.set(3, 3, 3);
    roof.position.y = 2;
    group.add(roof);
    const chimney = await this.model("free/chimney", 1.5);
    chimney.position.set(0.7, 3, 0);
    group.add(chimney);
    group.scale.setScalar(1.45);
    return group;
  }
  async zone(zone) {
    if (this.zones.has(zone.id)) return;
    if (!this.zoneTasks.has(zone.id))
      this.zoneTasks.set(
        zone.id,
        this.buildZone(zone).finally(() => this.zoneTasks.delete(zone.id)),
      );
    return this.zoneTasks.get(zone.id);
  }
  async buildZone(zone) {
    const root = new T.Group();
    const put = (object, x = 0, z = 0, height = 0) => this.attach(root, object, x, z, height);
    if (zone.id === "inspiration") {
      put(await this.model("free/oak-wide", 8.5));
      for (const x of [-2.8, 2.8]) put(this.bench(), x, 0.5);
      for (let i = 0; i < 5; i++) {
        const star = await this.model("free/star", 0.55);
        put(star, (i - 2) * 0.85, 1.5, 3.1 + Math.sin(i * 1.7) * 0.5);
      }
      put(await this.model("free/star", 0.9), 0, 3.3, 0.7);
    } else if (zone.id === "studio") {
      put(await this.cottage());
      const desk = this.bench(true);
      put(desk, 0, 3.6);
      const board = this.box([1.4, 1, 0.12], this.materials.cream);
      board.rotation.x = -0.22;
      put(board, -0.5, 3.3, 1.65);
      put(await this.model("free/gem", 0.55), 0.8, 3.6, 1.3);
      put(await this.model("free/banner-green", 1.7), -2.7, 0.8);
      put(this.flowers(), 3, 1.4);
    } else if (zone.id === "works") {
      for (let i = 0; i < 3; i++) {
        const theme = new T.Group();
        const base = new T.Mesh(new T.CylinderGeometry(1.15, 1.25, 0.3, 32), this.materials.cream);
        base.position.y = 0.15; theme.add(base);
        if (i === 0) {
          const rock = await this.model("free/stone", 0.9); rock.position.set(-0.25, 0.3, 0); theme.add(rock);
          const tree = await this.model("free/pine", 1.5); tree.position.set(0.45, 0.3, -0.2); theme.add(tree);
          const flag = await this.model("free/flag", 1); flag.position.set(-0.4, 1, 0); theme.add(flag);
        } else if (i === 1) {
          for (let j = 0; j < 3; j++) {
            const cube = this.box([0.65, 0.65, 0.65], [this.materials.leaf, this.materials.pink, this.materials.yellow][j]);
            cube.position.set((j - 1) * 0.6, 0.65 + (j === 1 ? 0.6 : 0), 0); theme.add(cube);
          }
        } else {
          const book = this.box([1.3, 0.18, 0.9], this.materials.pink); book.position.y = 0.4; theme.add(book);
          const gate = await this.model("free/fence-gate", 1); gate.position.set(0, 0.5, -0.2); theme.add(gate);
          const heart = await this.model("free/heart", 0.65); heart.position.set(0, 1.5, 0); theme.add(heart);
        }
        theme.name = `theme-${i}`;
        put(theme, (i - 1) * 2.9, i === 1 ? -0.6 : 0);
      }
      put(this.flowers(), -4, 1.7); put(this.flowers(), 4, 1.7);
    } else if (zone.id === "share") {
      for (let i = 0; i < 3; i++) {
        const deck = await this.model("free/planks");
        deck.scale.set(2.3, 1.5, 1.5);
        put(deck, 0, -i * 1.4, 0.14);
        for (const x of [-0.9, 0.9]) {
          const post = this.box([0.14, 0.7, 0.14], this.materials.wood);
          put(post, x, -i * 1.4, 0.12);
        }
      }
      put(await this.model("boat", 0.8), 1.5, -4.5, 0.12);
      const mailbox = new T.Group();
      const post = this.box([0.16, 1.1, 0.16], this.materials.wood); post.position.y = 0.55; mailbox.add(post);
      const box = this.box([0.8, 0.6, 0.65], this.materials.leaf); box.position.y = 1.3; mailbox.add(box);
      const slot = this.box([0.48, 0.06, 0.03], this.materials.cream); slot.position.set(0, 1.35, 0.34); mailbox.add(slot);
      put(mailbox, 1.8, 2.6);
      put(this.flowers(), -2, 1.7);
    } else if (zone.id === "quiet") {
      put(this.bench(), 0, 0);
      const flowers = [];
      for (let i = 0; i < 7; i++) {
        const flower = this.flowers(i % 2 ? this.materials.pink : this.materials.yellow);
        put(flower, Math.sin(i * 2.4) * 2.5, i < 3 ? 3 : -1.8 + Math.cos(i) * 1.2);
        flowers.push(flower);
      }
      root.userData.flowers = flowers;
      const pole = this.box([0.12, 2.6, 0.12], this.materials.wood); put(pole, -2.5, -0.5, 1.3);
      const sails = await this.model("free/windmill", 1.5);
      sails.rotation.y = -Math.PI / 2;
      put(sails, -2.5, -0.35, 2.5);
      root.userData.sails = sails;
    } else if (zone.id === "pocket") {
      put(await this.model("pocket", 4));
      put(this.bench(), -2.7, 1.2);
      put(this.flowers(), 2.8, 1.5);
    } else {
      put(await this.model("welcome", 4.4));
      const label = new T.Mesh(new T.PlaneGeometry(5, 1), new T.MeshBasicMaterial({ map: this.labelTexture("MIMICO"), transparent: true }));
      put(label, 0, 0.24, 3.9);
      put(this.flowers(), -3, 0.5); put(this.flowers(), 3, 0.5);
    }
    place(root, zone.point, 0.02);
    root.traverse((c) => {
      c.userData.zone = zone.id;
      if (c.isMesh) c.castShadow = !this.low && !c.isInstancedMesh;
    });
    if (
      import.meta.env?.DEV &&
      new URLSearchParams(location.search).has("graybox")
    )
      root.traverse((c) => {
        if (c.isMesh)
          c.material = new T.MeshStandardMaterial({ color: "#dddccf" });
      });
    this.zones.set(zone.id, root);
    this.interactive.push(root);
    this.scene.add(root);
  }
  attach(root, object, x = 0, z = 0, height = 0) {
    const normal = new T.Vector3(x, RADIUS, z).normalize();
    object.position.copy(normal.clone().multiplyScalar(RADIUS + height).sub(new T.Vector3(0, RADIUS, 0)));
    object.quaternion.premultiply(new T.Quaternion().setFromUnitVectors(UP, normal));
    root.add(object);
    return object;
  }
  box(size, material) {
    return new T.Mesh(new T.BoxGeometry(...size), material);
  }
  bench(table = false) {
    const group = new T.Group(), y = table ? 1.15 : 0.65;
    const top = this.box([2.2, 0.18, table ? 1 : 0.7], this.materials.wood);
    top.position.y = y; group.add(top);
    for (const x of [-0.8, 0.8]) {
      const leg = this.box([0.18, y, 0.5], this.materials.leaf);
      leg.position.set(x, y / 2 - 0.07, 0); group.add(leg);
    }
    if (!table) {
      const back = this.box([2.2, 0.4, 0.14], this.materials.wood);
      back.position.set(0, 1.12, -0.32); group.add(back);
    }
    return group;
  }
  flowers(material = this.materials.pink) {
    const group = new T.Group();
    this.petalGeometry ||= new T.SphereGeometry(0.14, 8, 6);
    this.stemGeometry ||= new T.CylinderGeometry(0.025, 0.025, 0.5, 5);
    const petals = new T.InstancedMesh(this.petalGeometry, material, 25);
    const stems = new T.InstancedMesh(this.stemGeometry, this.materials.leaf, 5);
    const cores = new T.InstancedMesh(this.petalGeometry, this.materials.yellow, 5);
    const dummy = new T.Object3D();
    for (let i = 0; i < 5; i++) {
      const x = Math.sin(i * 2.4) * 0.45, z = Math.cos(i * 2.4) * 0.4, y = 0.45 + (i % 3) * 0.08;
      dummy.position.set(x, y / 2, z); dummy.scale.set(1, y / 0.5, 1); dummy.updateMatrix(); stems.setMatrixAt(i, dummy.matrix);
      for (let j = 0; j < 5; j++) {
        dummy.position.set(x + Math.cos(j * Math.PI * 0.4) * 0.14, y, z + Math.sin(j * Math.PI * 0.4) * 0.14);
        dummy.scale.set(1, 0.55, 1); dummy.updateMatrix(); petals.setMatrixAt(i * 5 + j, dummy.matrix);
      }
      dummy.position.set(x, y + 0.04, z); dummy.scale.setScalar(0.7); dummy.updateMatrix(); cores.setMatrixAt(i, dummy.matrix);
    }
    group.add(petals, stems, cores);
    return group;
  }
  labelTexture(text) {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 128;
    const c = canvas.getContext("2d");
    c.fillStyle = "#326044";
    c.font = "800 72px system-ui";
    c.textAlign = "center";
    c.textBaseline = "middle";
    c.fillText(text, 256, 64);
    const texture = new T.CanvasTexture(canvas);
    texture.colorSpace = T.SRGBColorSpace;
    return texture;
  }
  onPath(point) {
    return this.routes.some(r => pathDistance(point, r.from, r.to) < r.width / 2 + 1.4) ||
      ZONES.some(z => distance(point, z.point) < 6.5 || distance(point, z.spawn) < 3) ||
      distance(point, this.pondPoint) < 6;
  }
  async decorate() {
    const groups = new Map();
    const add = (name, point, height, turn = 0) => {
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push({ point, height, turn });
    };
    // Authored groves alternate with open meadows; no uniform global scatter.
    const groves = [[-29, 57], [33, 60], [-78, 39], [82, 42], [-108, -2], [122, 3], [-145, 48], [158, -37], [-120, -53], [32, -46]];
    groves.forEach((center, cluster) => {
      for (let i = 0; i < 19; i++) {
        const angle = i * 2.39996 + cluster, radius = Math.sqrt((i + 0.5) / 19) * 6;
        const point = offsetPoint(center, Math.cos(angle) * radius, Math.sin(angle) * radius);
        if (this.onPath(point)) continue;
        const name = i < 7 ? (cluster % 3 === 0 ? "oak-wide" : "oak") : i < 13 ? "bush-round" : i < 17 ? "grass" : "stone";
        add(name, point, i < 7 ? 2.7 + ((i * 7 + cluster) % 5) * 0.22 : i < 13 ? 0.7 + (i % 3) * 0.12 : 0.5, angle);
      }
    });
    for (const zone of ZONES) {
      add("lantern", offsetPoint(zone.point, -2.4, 3.3), 1.7);
      for (const side of [-1, 1]) {
        add("bush-round", offsetPoint(zone.point, side * 4.8, -1), 0.85);
        add("grass", offsetPoint(zone.point, side * 4.6, 1), 0.5);
      }
    }
    const results = await Promise.allSettled(
      [...groups].map(async ([name, items]) => {
        if (this.decorationNames.has(name)) return;
        // Prefix reduction must stay distributed over both hemispheres in low quality.
        items = items
          .map((item, i) => ({ item, key: (i * 0.61803398875) % 1 }))
          .sort((a, b) => a.key - b.key)
          .map((v) => v.item);
        const source = await this.model(`free/${name}`, 1);
        source.updateMatrixWorld(true);
        source.traverse((child) => {
          if (!child.isMesh) return;
          const batch = new T.InstancedMesh(
            child.geometry,
            this.foliageMaterial(child.material),
            items.length,
          );
          const dummy = new T.Object3D();
          items.forEach(({ point, height, turn }, i) => {
            place(dummy, point, 0, height, turn);
            dummy.updateMatrix();
            batch.setMatrixAt(
              i,
              new T.Matrix4().multiplyMatrices(dummy.matrix, child.matrixWorld),
            );
          });
          batch.receiveShadow = true;
          batch.userData.fullCount = items.length;
          this.decorations.push(batch);
          this.scene.add(batch);
        });
        if (
          ["oak", "oak-wide", "stone"].includes(
            name,
          )
        )
          for (const [index, item] of items.entries())
            this.obstacles.push({
              index, fullCount: items.length,
              normal: normalAt(item.point),
              limit: Math.cos((0.4 + item.height * (name.startsWith("stone") ? 0.3 : 0.12)) / RADIUS),
            });
        this.decorationNames.add(name);
      }),
    );
    this.quality(this.low);
    return results.filter((r) => r.status === "rejected").length;
  }
  foliageMaterial(source) {
    const material = source.clone(), grade = source.onBeforeCompile;
    material.onBeforeCompile = shader => {
      grade(shader);
      Object.assign(shader.uniforms, { fadeCamera: this.fadeCamera, fadeTarget: this.fadeTarget, fadeEnabled: this.fadeEnabled });
      shader.vertexShader = "uniform vec3 fadeCamera; uniform vec3 fadeTarget; uniform float fadeEnabled; varying float cover;\n" + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace("#include <project_vertex>", `#include <project_vertex>
        vec3 center = (modelMatrix * instanceMatrix * vec4(0., 0., 0., 1.)).xyz;
        vec3 sight = normalize(fadeTarget - fadeCamera);
        float along = dot(center - fadeCamera, sight);
        float separation = length(center - fadeCamera - sight * along);
        cover = fadeEnabled * step(0., along) * step(along, length(fadeTarget - fadeCamera) - 0.8) * (1. - smoothstep(1.2, 3.2, separation));
      `);
      shader.fragmentShader = "varying float cover;\n" + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace("#include <alphatest_fragment>", `#include <alphatest_fragment>
        float screenDoor = fract(dot(floor(gl_FragCoord.xy), vec2(0.75487766, 0.56984029)));
        if (screenDoor < cover * 0.78) discard;
      `);
    };
    material.customProgramCacheKey = () => "mimico-foliage-visibility-v1";
    return material;
  }
  move(input, camera, dt) {
    if (
      !this.position.toArray().every(Number.isFinite) ||
      Math.abs(this.position.length() - RADIUS) > 2
    )
      this.teleport(coordinates(this.safe));
    const up = this.position.clone().normalize();
    const right = new T.Vector3()
      .setFromMatrixColumn(camera.matrixWorld, 0)
      .projectOnPlane(up)
      .normalize();
    if (right.lengthSq() < 0.1) right.crossVectors(this.north, up).normalize();
    const forward = new T.Vector3().crossVectors(up, right).normalize();
    const direction = right
      .multiplyScalar(input.x)
      .addScaledVector(forward, input.y);
    const strength = Math.min(1, direction.length()),
      moving = strength > 0.05;
    const candidate = moving
      ? stepSurface(this.position, direction, SPEED * strength * dt)
      : this.position.clone();
    const normal = candidate.clone().normalize();
    if (
      canStand(...coordinates(candidate)) &&
      !this.obstacles.some((o) => o.active && normal.dot(o.normal) > o.limit)
    )
      this.position.copy(candidate);
    const finite = this.position.toArray().every(Number.isFinite);
    if (
      !finite ||
      this.position.length() < RADIUS * 0.5 ||
      this.position.length() > RADIUS * 1.5
    )
      this.position.copy(this.safe);
    this.position.setLength(RADIUS);
    this.safe.copy(this.position);
    const nextUp = this.position.clone().normalize(),
      transport = new T.Quaternion().setFromUnitVectors(up, nextUp);
    this.north.applyQuaternion(transport).projectOnPlane(nextUp).normalize();
    this.heading.applyQuaternion(transport).projectOnPlane(nextUp).normalize();
    if (moving) {
      const target = direction.clone().projectOnPlane(nextUp).normalize();
      this.heading.lerp(target, 1 - Math.exp(-15 * dt)).normalize();
    }
    // Cannon retains a spherical ground/contact body; locomotion uses a constrained great-circle step.
    const p = this.position.clone().setLength(RADIUS + 0.4);
    this.body.position.set(p.x, p.y, p.z);
    this.body.velocity.set(0, 0, 0);
    this.body.aabbNeedsUpdate = true;
    this.physics.step(1 / 60, Math.min(dt, 0.05), 3);
    if (this.avatar) {
      this.avatar.position.copy(this.position);
      const x = new T.Vector3().crossVectors(nextUp, this.heading).normalize();
      this.avatar.quaternion.setFromRotationMatrix(
        new T.Matrix4().makeBasis(x, nextUp, this.heading),
      );
      if (moving) {
        this.play("Walk");
        this.actions.Walk.timeScale = 2.68 * strength;
      } else if (this.elapsed >= this.reactUntil) this.play("Idle");
      this.spark.visible = this.animation === "Point";
    }
    return moving;
  }
  teleport(point) {
    this.position.copy(surface(point));
    this.safe.copy(this.position);
    this.north.copy(frameAt(point).north);
    this.heading.copy(this.north).negate();
    const p = this.position.clone().setLength(RADIUS + 0.4);
    this.body.position.set(p.x, p.y, p.z);
    this.body.velocity.set(0, 0, 0);
    if (this.avatar) {
      place(this.avatar, point, 0, 1.5);
      this.reactUntil = 0;
      this.play("Idle");
    }
  }
  react() {
    this.play("Point");
    this.reactUntil = this.elapsed + 1.2;
  }
  async effect(id, index) {
    const zone = this.zones.get(id);
    if (!zone) return;
    this.react();
    const ring = new T.Mesh(
      new T.RingGeometry(0.6, 0.75, 40),
      new T.MeshBasicMaterial({
        color: "#fff3b8",
        transparent: true,
        side: T.DoubleSide,
        depthWrite: false,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(FOCUS_OFFSETS[id][0], 0.2, FOCUS_OFFSETS[id][1]);
    zone.add(ring);
    this.effects.push({ object: ring, start: this.elapsed, kind: "ring" });
    if (id === "share") {
      const boat = await this.model("boat", 1);
      this.attach(zone, boat, 0.8, -3, 0.14);
      this.effects.push({ object: boat, start: this.elapsed, kind: "boat" });
    }
    if (id === "inspiration" || id === "studio") {
      const item = await this.model(
        `free/${["star", "gem", "heart"][index % 3]}`,
        1.4,
      );
      item.position.set(0, 2, 3);
      zone.add(item);
      this.effects.push({ object: item, start: this.elapsed, kind: "idea" });
    }
    if (id === "works")
      zone.children.forEach((c) => {
        if (c.name.startsWith("theme-"))
          c.scale.multiplyScalar(c.userData.selected ? 1 / 1.25 : 1);
        c.userData.selected = c.name === `theme-${index % 3}`;
        if (c.userData.selected) c.scale.multiplyScalar(1.25);
      });
    if (id === "quiet") { zone.userData.spinUntil = this.elapsed + 4; zone.userData.bloomStart = this.elapsed; }
    if (id === "pocket")
      zone.traverse((c) => {
        if (c.isMesh && c.name.startsWith("screen-")) {
          if (!c.userData.ownMaterial) {
            c.material = c.material.clone();
            c.userData.ownMaterial = true;
          }
          c.material.emissive.set(index % 2 ? "#79c5ba" : "#f1d679");
          c.material.emissiveIntensity = 0.7;
        }
      });
  }
  update(dt, reduced) {
    this.elapsed += dt;
    this.mixer?.update(reduced && this.animation === "Idle" ? 0 : dt);
    this.clouds.visible = !this.low;
    const quiet = this.zones.get("quiet");
    if (quiet?.userData.spinUntil > this.elapsed && !reduced)
      quiet.userData.sails.rotateX(dt * 2);
    if (quiet?.userData.bloomStart !== undefined) {
      const t = this.elapsed - quiet.userData.bloomStart;
      quiet.userData.flowers.forEach((flower, i) => {
        const pulse = Math.max(0, Math.sin(Math.min(Math.PI, Math.max(0, t - i * 0.18) * 2)));
        flower.scale.setScalar(1 + pulse * 0.3);
      });
    }
    for (const e of [...this.effects]) {
      const t = this.elapsed - e.start;
      if (e.kind === "ring") {
        e.object.scale.setScalar(1 + t * 2);
        e.object.material.opacity = Math.max(0, 1 - t / 1.5);
      } else if (e.kind === "boat") {
        e.object.quaternion.identity();
        this.attach(e.object.parent, e.object, 0.8 + t * 0.35, -3 - t * 0.7, 0.14);
      } else {
        e.object.position.y =
          2 + Math.sin((Math.min(t, 2) / 2) * Math.PI) * 1.5;
        if (!reduced) e.object.rotation.y += dt;
      }
      if (t > (e.kind === "ring" ? 1.5 : 4)) {
        e.object.removeFromParent();
        if (e.kind === "ring") {
          e.object.geometry.dispose();
          e.object.material.dispose();
        }
        this.effects.splice(this.effects.indexOf(e), 1);
      }
    }
  }
  quality(low) {
    this.low = low;
    for (const obstacle of this.obstacles) obstacle.active = !low || obstacle.index < Math.ceil(obstacle.fullCount * 0.6);
    for (const d of this.decorations) {
      d.count = low
        ? Math.ceil(d.userData.fullCount * 0.6)
        : d.userData.fullCount;
      d.castShadow = !low;
    }
    for (const z of this.zones.values())
      z.traverse((c) => {
        if (c.isMesh) c.castShadow = !low && !c.isInstancedMesh;
      });
  }
}
